import { Controller } from "@hotwired/stimulus";
import snsWebSdk from "@sumsub/websdk";
import { rrFetch } from "../utils/rr_fetch";

// Map Sumsub SDK error descriptions to bottomsheet URLs.
// Add new entries here to handle additional error states without touching launch().
const SUMSUB_ERROR_BOTTOMSHEETS = {
  "Applicant is deactivated": "/account-verification/kyc-error/deactivated",
};

class BottomsheetError extends Error {
  constructor(url) {
    super("bottomsheet");
    this.bottomsheetUrl = url;
  }
}

export default class SumsubTestKycController extends Controller {
  static values = {
    csrfToken: String,
    tokenUrl: String,
    customizationName: String,
  };

  static targets = ["status", "startButton", "sdkContainer", "retryButton", "errorState"];

  connect() {
    this._onResize = this.syncContainerHeight.bind(this);
    window.addEventListener("resize", this._onResize, { passive: true });
    requestAnimationFrame(() => {
      this.syncContainerHeight();
      this.start();
    });
  }

  disconnect() {
    if (this._onResize) {
      window.removeEventListener("resize", this._onResize);
    }
  }

  start() {
    if (this.hasStartButtonTarget) {
      this.startButtonTarget.classList.add("hidden");
    }
    if (this.hasSdkContainerTarget) {
      this.sdkContainerTarget.classList.remove("hidden");
      this.syncContainerHeight();
    }
    this.launch();
  }

  syncContainerHeight() {
    if (!this.hasSdkContainerTarget) return;
    const rootRect = this.element.getBoundingClientRect();
    const containerRect = this.sdkContainerTarget.getBoundingClientRect();
    const availableHeight = Math.floor(rootRect.bottom - containerRect.top);
    if (availableHeight > 240) {
      this.sdkContainerTarget.style.height = `${availableHeight}px`;
    }
  }

  showStatus(message) {
    this.showError(message);
  }

  showError(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message;
    if (this.hasErrorStateTarget) this.errorStateTarget.classList.remove("hidden");
    if (this.hasSdkContainerTarget) this.sdkContainerTarget.classList.add("hidden");
  }

  hideError() {
    if (this.hasErrorStateTarget) this.errorStateTarget.classList.add("hidden");
    if (this.hasStatusTarget) this.statusTarget.textContent = "";
    if (this.hasSdkContainerTarget) this.sdkContainerTarget.classList.remove("hidden");
  }

  _fetchBottomsheet(url) {
    rrFetch(url, {
      method: "GET",
      headers: { "X-Requested-With": "XMLHttpRequest", Accept: "text/vnd.turbo-stream.html" },
      credentials: "same-origin",
    })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(html => Turbo.renderStreamMessage(html))
      .catch(e => {
        console.error("Failed to fetch KYC error bottomsheet:", e);
      });
  }

  _handleSdkError(err) {
    const description = err?.description || (typeof err === "string" ? err : null);
    const url = description && SUMSUB_ERROR_BOTTOMSHEETS[description];
    if (url) {
      this._fetchBottomsheet(url);
      return;
    }
    const msg = description || err?.code || "Verification error";
    this.showStatus(msg);
  }

  async fetchToken() {
    const res = await fetch(this.tokenUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
        Accept: "application/json",
      },
      body: "{}",
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (data.bottomsheet_url) throw new BottomsheetError(data.bottomsheet_url);
      throw new Error(data.error || res.statusText || "Token request failed");
    }
    if (!data.token) {
      throw new Error("No token in response");
    }
    return data.token;
  }

  async launch() {
    this.hideError();
    try {
      const accessToken = await this.fetchToken();
      const instance = snsWebSdk
        .init(accessToken, () => this.fetchToken())
        .withConf({
          lang: "en",
          theme: "dark",
          customCssStr: `
            :root { --border-radius: 12px; }
            html, body { height: 100% !important; min-height: 100% !important; }
            body { border-radius: 12px; overflow: hidden; }
          `,
          customizationName: this.customizationNameValue, // spec of wanted pages is not saved when this is set, check that out
        })
        .withOptions({ addViewportTag: false, adaptIframeHeight: false })
        .on("idCheck.onStepCompleted", () => {})
        .on("idCheck.onError", (err) => {
          console.warn("Sumsub WebSDK error", err);
          this._handleSdkError(err);
        })
        .build();
      instance.launch("#sumsub-websdk-container");
    } catch (e) {
      if (e instanceof BottomsheetError) {
        this._fetchBottomsheet(e.bottomsheetUrl);
      } else {
        console.error(e);
        this.showError(e.message);
      }
    }
  }
}
